import { create } from 'zustand';
import { apiClient } from '@services/api';
import { updateMetaThemeColor } from '../utils/themeColor';

/** Parse the `did` (device-id) claim from the guest JWT stored in localStorage. */
function getDeviceIdFromToken(): number | null {
  try {
    const token = localStorage.getItem('gcxp_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.did ? Number(payload.did) : null;
  } catch {
    return null;
  }
}

interface ThemeColors {
  background?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  textColor?: string;
}

interface SessionInfo {
  sessionId: string;
  hotelId: number;
  hotelSlug: string;
  hotelName: string;
  scope: string;
  scopeValue: string;
  language: string;
  expiresAt: Date;
  availableModules: string[];
  themeColors?: ThemeColors;
  trustLevel: number;
  verifiedByDeviceId?: number | null;
  /** Per-DEVICE trust: true when THIS device has cleared the room-phone gate and may order/charge.
   *  Distinct from session-wide trustLevel — gate ordering/folio on this, not trustLevel. */
  deviceTrusted?: boolean;
  /** True when THIS device is the room's Primary (the reservation guest) — proven control of the room
   *  number, or staff-set. NOT "first to verify". */
  isPrimaryDevice?: boolean;
  availableStations?: { id: number; name: string; iconEmoji?: string; color?: string }[];
  sessionType?: string;  // e.g., "hotel_room", "restaurant", "spa"
  displayLabel?: string; // Human-readable label for non-room sessions
  // Session type settings
  requiresIdentifier?: boolean;  // If true, guest must enter name
  identifierLabel?: string;       // Label for identifier field (e.g., "Name")
  requiresPhoneVerification?: boolean; // If true, show verification prompt
  allowMultipleDevices?: boolean; // If true, show "Add Device" option
}

interface SetSessionInput {
  sessionId?: string;
  hotelId?: number;
  hotelSlug: string;
  hotelName?: string;
  scope?: string;
  scopeValue?: string;
  language?: string;
  expiresAt?: string | Date;
  availableModules?: string[];
  themeColors?: ThemeColors;
  trustLevel?: number;
  availableStations?: { id: number; name: string; iconEmoji?: string; color?: string }[];
  sessionType?: string;
  displayLabel?: string;
  // Session type settings
  requiresIdentifier?: boolean;
  identifierLabel?: string;
  requiresPhoneVerification?: boolean;
  allowMultipleDevices?: boolean;
}

interface SessionState {
  isLoading: boolean;
  isValid: boolean;
  session: SessionInfo | null;
  error: string | null;
  personalName: string | null;
  needsNamePrompt: boolean;
  
  validateSession: () => Promise<void>;
  refreshSession: () => Promise<void>;
  setSession: (session: SetSessionInput) => void;
  updateLanguage: (languageCode: string) => Promise<void>;
  clearSession: () => void;
  setPersonalName: (name: string) => void;
  dismissNamePrompt: () => void;
  updateTrustLevel: (level: number) => void;
  /** True when this device was the one that completed OTP verification. */
  isVerifierDevice: () => boolean;
  /** True when THIS device may order/charge (cleared the room-phone gate). Gate ordering/folio on this. */
  isDeviceTrusted: () => boolean;
  /** Get the current device ID from the JWT token */
  getDeviceId: () => number | null;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  isLoading: true,
  isValid: false,
  session: null,
  error: null,
  personalName: localStorage.getItem('gcxp_personal_name'),
  needsNamePrompt: false,

  validateSession: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const token = localStorage.getItem('gcxp_token');
      if (!token) {
        set({ isLoading: false, isValid: false, session: null });
        return;
      }

      // Get hotel slug from localStorage (stored during activation) or fallback to URL
      let hotelSlug = localStorage.getItem('gcxp_hotel_slug');
      if (!hotelSlug) {
        // Fallback: try to extract from URL path
        const pathParts = window.location.pathname.split('/');
        hotelSlug = pathParts[1] || '';
      }

      const response = await apiClient.get(`/api/v1/${hotelSlug}/session/validate`);
      
      if (response.data.success && response.data.data.isValid) {
        const sessionData = response.data.data.session;
        const existingName = localStorage.getItem('gcxp_personal_name');
        
        // Sync expiry timestamps to localStorage (picks up admin extensions)
        if (sessionData.expiresAt) {
          localStorage.setItem('gcxp_expires_at', sessionData.expiresAt);
          localStorage.setItem('gcxp_checkout_at', sessionData.expiresAt);
        }
        
        // Only prompt for name if session requires it AND user hasn't entered one
        const requiresIdentifier = sessionData.requiresIdentifier ?? true; // Default true for backward compat
        
        set({
          isLoading: false,
          isValid: true,
          personalName: existingName,
          needsNamePrompt: requiresIdentifier && !existingName,
          session: {
            ...sessionData,
            expiresAt: new Date(sessionData.expiresAt),
            trustLevel: sessionData.trustLevel ?? 0,
            verifiedByDeviceId: sessionData.verifiedByDeviceId ?? null,
            deviceTrusted: sessionData.deviceTrusted ?? false,
            isPrimaryDevice: sessionData.isPrimaryDevice ?? false,
            // Include session type settings with defaults
            requiresIdentifier: sessionData.requiresIdentifier ?? true,
            identifierLabel: sessionData.identifierLabel,
            requiresPhoneVerification: sessionData.requiresPhoneVerification ?? false,
            allowMultipleDevices: sessionData.allowMultipleDevices ?? true,
          },
        });
        
        // Store language for API client
        if (sessionData.language) {
          localStorage.setItem('gcxp_language', sessionData.language);
        }
        
        // Update document direction based on language
        updateDocumentDirection(sessionData.language);
        
        // Apply theme colors if available
        if (sessionData.themeColors) {
          applyThemeColors(sessionData.themeColors);
        }
      } else {
        set({ isLoading: false, isValid: false, session: null });
      }
    } catch (error: any) {
      set({ 
        isLoading: false, 
        isValid: false, 
        error: error.message || 'Failed to validate session' 
      });
    }
  },

  // Silent background check — no loading spinner, only updates state if changed
  refreshSession: async () => {
    try {
      const token = localStorage.getItem('gcxp_token');
      if (!token) {
        const { isValid } = get();
        if (isValid) set({ isValid: false, session: null });
        return;
      }

      let hotelSlug = localStorage.getItem('gcxp_hotel_slug');
      if (!hotelSlug) {
        const pathParts = window.location.pathname.split('/');
        hotelSlug = pathParts[1] || '';
      }

      const response = await apiClient.get(`/api/v1/${hotelSlug}/session/validate`);

      if (response.data.success && response.data.data.isValid) {
        const sessionData = response.data.data.session;

        // Sync expiry timestamps (picks up admin extensions)
        if (sessionData.expiresAt) {
          localStorage.setItem('gcxp_expires_at', sessionData.expiresAt);
          localStorage.setItem('gcxp_checkout_at', sessionData.expiresAt);
        }

        const { session } = get();
        // Check if important data changed - expiresAt, trustLevel, deviceTrusted, modules, or stations
        const modulesChanged = JSON.stringify(session?.availableModules) !== JSON.stringify(sessionData.availableModules);
        const stationsChanged = JSON.stringify(session?.availableStations) !== JSON.stringify(sessionData.availableStations);
        const expiresAtChanged = !session || session.expiresAt.getTime() !== new Date(sessionData.expiresAt).getTime();
        const trustLevelChanged = session?.trustLevel !== (sessionData.trustLevel ?? 0);
        // deviceTrusted must be in the diff so a device verifying to order re-renders the gate live
        const deviceTrustedChanged = (session?.deviceTrusted ?? false) !== (sessionData.deviceTrusted ?? false);

        if (expiresAtChanged || trustLevelChanged || deviceTrustedChanged || modulesChanged || stationsChanged) {
          set({
            isValid: true,
            session: {
              ...sessionData,
              expiresAt: new Date(sessionData.expiresAt),
              trustLevel: sessionData.trustLevel ?? 0,
              verifiedByDeviceId: sessionData.verifiedByDeviceId ?? null,
              deviceTrusted: sessionData.deviceTrusted ?? false,
              isPrimaryDevice: sessionData.isPrimaryDevice ?? false,
            },
          });
        }
      } else {
        // Session no longer valid — show expired screen
        set({ isValid: false, session: null });
      }
    } catch {
      // API error (401 = expired/revoked) — silently invalidate
      const { isValid } = get();
      if (isValid) {
        set({ isValid: false, session: null });
      }
    }
  },

  updateLanguage: async (languageCode: string) => {
    const { session } = get();
    if (!session) return;

    try {
      await apiClient.patch(`/api/v1/${session.hotelSlug}/session/language`, {
        languageCode,
      });
      
      set({
        session: { ...session, language: languageCode },
      });
      
      localStorage.setItem('gcxp_language', languageCode);
      updateDocumentDirection(languageCode);
    } catch (error) {
      console.error('Failed to update language:', error);
    }
  },

  setSession: (sessionData) => {
    const expiresAt = sessionData.expiresAt 
      ? (typeof sessionData.expiresAt === 'string' ? new Date(sessionData.expiresAt) : sessionData.expiresAt)
      : new Date(Date.now() + 24 * 60 * 60 * 1000); // Default 24h
    
    // Check if personal name is already set (returning device)
    const existingName = localStorage.getItem('gcxp_personal_name');
    
    set({
      isLoading: false,
      isValid: true,
      session: {
        sessionId: sessionData.sessionId || '',
        hotelId: sessionData.hotelId || 0,
        hotelSlug: sessionData.hotelSlug,
        hotelName: sessionData.hotelName || '',
        scope: sessionData.scope || 'room',
        scopeValue: sessionData.scopeValue || '',
        language: sessionData.language || 'en',
        expiresAt,
        availableModules: sessionData.availableModules || [],
        themeColors: sessionData.themeColors,
        trustLevel: sessionData.trustLevel ?? 0,
        verifiedByDeviceId: null,
        availableStations: sessionData.availableStations || [],
        sessionType: sessionData.sessionType,
        displayLabel: sessionData.displayLabel,
      },
      personalName: existingName,
      needsNamePrompt: !existingName, // Prompt if no name set yet
      error: null,
    });
    
    // Apply theme colors if available
    if (sessionData.themeColors) {
      applyThemeColors(sessionData.themeColors);
    }
  },

  clearSession: () => {
    localStorage.removeItem('gcxp_token');
    localStorage.removeItem('gcxp_session_id');
    localStorage.removeItem('gcxp_hotel_slug');
    localStorage.removeItem('gcxp_expires_at');
    localStorage.removeItem('gcxp_checkout_at');
    localStorage.removeItem('gcxp_hotel_timezone');
    localStorage.removeItem('gcxp_language');
    localStorage.removeItem('gcxp_theme_colors');
    localStorage.removeItem('gcxp_personal_name');
    localStorage.removeItem('gcxp_guest_order_name');
    
    // Clear theme CSS variables
    const root = document.documentElement;
    root.style.removeProperty('--theme-background');
    root.style.removeProperty('--theme-color-1');
    root.style.removeProperty('--theme-color-2');
    root.style.removeProperty('--theme-color-3');
    root.style.removeProperty('--theme-text-color');
    root.classList.remove('hotel-themed');
    // Reset mobile Safari status-bar to default (matches default top-nav-bar background)
    updateMetaThemeColor(null);

    set({ isLoading: false, isValid: false, session: null, personalName: null, needsNamePrompt: false });
  },

  setPersonalName: (name: string) => {
    localStorage.setItem('gcxp_personal_name', name);
    // Also set as the order name for breakfast orders (backwards compatible)
    localStorage.setItem('gcxp_guest_order_name', name);
    set({ personalName: name, needsNamePrompt: false });
    
    // Sync to server (best-effort, don't fail if this fails)
    const hotelSlug = localStorage.getItem('gcxp_hotel_slug');
    if (hotelSlug) {
      apiClient.patch(`/api/v1/${hotelSlug}/session/device-name`, { personalName: name })
        .catch(() => {
          // Ignore errors - local storage is primary
        });
    }
  },

  dismissNamePrompt: () => {
    set({ needsNamePrompt: false });
  },

  updateTrustLevel: (level: number) => {
    const { session } = get();
    if (session) {
      set({ session: { ...session, trustLevel: level } });
    }
  },

  // "Am I the room's Primary device?" — now driven by the real DeviceRole.Primary signal (proven
  // control of the room number, or staff-set), NOT by who happened to verify the OTP first.
  isVerifierDevice: () => get().session?.isPrimaryDevice === true,

  isDeviceTrusted: () => get().session?.deviceTrusted === true,

  /** Get the current device ID from the JWT token */
  getDeviceId: () => getDeviceIdFromToken(),
}));

function updateDocumentDirection(language: string) {
  const rtlLanguages = ['he', 'ar'];
  const dir = rtlLanguages.includes(language) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', language);
}

function applyThemeColors(themeColors: ThemeColors) {
  const root = document.documentElement;
  
  // Store theme colors in localStorage for instant load on next visit
  try {
    localStorage.setItem('gcxp_theme_colors', JSON.stringify(themeColors));
  } catch (e) {
    console.warn('Failed to store theme colors:', e);
  }
  
  if (themeColors.background) {
    root.style.setProperty('--theme-background', themeColors.background);
  }
  if (themeColors.color1) {
    root.style.setProperty('--theme-color-1', themeColors.color1);
  }
  if (themeColors.color2) {
    root.style.setProperty('--theme-color-2', themeColors.color2);
  }
  if (themeColors.color3) {
    root.style.setProperty('--theme-color-3', themeColors.color3);
  }
  if (themeColors.textColor) {
    root.style.setProperty('--theme-text-color', themeColors.textColor);
  }

  // Mark that a custom theme is applied
  root.classList.add('hotel-themed');

  // Keep mobile Safari status-bar / Dynamic Island in sync with the hotel theme
  updateMetaThemeColor(themeColors.color1 || themeColors.background);
}
